import AdvancedController from "./advanced-controller"
import { debounce } from "../utils/utils"

const Cnf = {
  defaultWeightUnit: 'kg',
  wrapperSelector: '.thwepo_phoenix_std_configuration',
  allStepRadioSelector: '.phoenix_std_kg_steps input, .phoenix_std_lbs_steps input',
  weightUnitRadioSelector: '.extra-options .phoenix_weight_unit input[type="radio"]',
  weightsListValueSel: "#phoenix-data-weights-list .product__custom-attribute__value",
  radiosThatTriggerUpdatesSelector: '.extra-options .phoenix_std_kg_steps input[type="radio"], .extra-options .phoenix_std_lbs_steps input[type="radio"], .extra-options .phoenix_weight_unit input[type="radio"]',
  customAttributes: {
    totalWeightId: 'phoenix-data-total-weight',
    totalPairsId: 'phoenix-data-total-pairs',
    weightsListId: 'phoenix-data-weights-list',
    valueClass: 'product__custom-attribute__value'
  },
  sliderFromSliderId: 'fromSlider',
  sliderToSliderId: 'toSlider',
  sliderFromInputId: 'fromInput',
  sliderToInputId: 'toInput',
  sliderColor: '#bdbdbd',
  sliderRangeColor: '#f06400',
  sliderAttributeKeys: ['min', 'max'],
  kg: {
    min: 5,
    max: 100,
    checkedStepRadioSelector: '#phoenix_std_kg_steps_field .-checked input',
    weightsSelector: 'phoenix_std_weights_kg',
  },
  lbs: {
    min: 10,
    max: 200,
    checkedStepRadioSelector: '#phoenix_std_lbs_steps_field .-checked input',
    weightsSelector: 'phoenix_std_weights_lbs',
  },
}

/**
 * Controller class for the Phoenix Dumbbell component.
 * Manages the behavior and functionality of the dumbbell slider and custom attributes.
 */
export default class PhoenixDumbellController extends AdvancedController {

  static targets = [];

  connect() {
    super.connect();

    this.debouncedUpdateCheckboxes = debounce(this.updateCheckboxes.bind(this), 500);
    this.weightUnit = Cnf.defaultWeightUnit
    this.lang = document.querySelector('html').getAttribute('lang')

    const phoenixConfigDiv = document.querySelector(Cnf.wrapperSelector);

    this.renderSlider(phoenixConfigDiv);
    this.renderCustomAttributes(phoenixConfigDiv);

    const { fromSlider, toSlider } = this.getTemplateElements()

    this.updateSliderFill(fromSlider, toSlider, toSlider);
    this.updateSliderAccessibility(toSlider); // TODO: ensure that updateSliderAccessibility is called always after updateSliderFill?! What does it do?

    this.registerSliderChangeEvents();
    this.registerStepChangeEvents();
    this.registerRadioChangeEvents();
    this.registerWeightChangeEvents();

    this.enhanceRadioButtonsWithCheckedClass();
  }

  /**
  * From either slider or input -> get the values, parse as float
  * @param {Object} currentFrom - the current from DOM element
  * @param {Object} currentTo - the current to DOM element
  * @returns {Array} - [from, to] as floats
  */
  getParsed(currentFrom, currentTo) {
    const from = parseFloat(currentFrom.value, 10);
    const to = parseFloat(currentTo.value, 10);
    return [from, to];
  }

  /**
  * Get the step value from the radio buttons depending on the weight unit
  * replaces _ with . and parses as float
  * @returns {Number} - the step value as float
  */
  getStepValueSetting() {
    const val = document.querySelector(Cnf[this.weightUnit].checkedStepRadioSelector).value
    return parseFloat(val.replace("_", "."))
  }

  /**
  * Get the template elements
  * @returns {Object} - the template elements as object with keys fromSlider, toSlider, fromInput, toInput
  */
  getTemplateElements() {
    return {
      fromSlider: document.querySelector(`#${Cnf.sliderFromSliderId}`),
      toSlider: document.querySelector(`#${Cnf.sliderToSliderId}`),
      fromInput: document.querySelector(`#${Cnf.sliderFromInputId}`),
      toInput: document.querySelector(`#${Cnf.sliderToInputId}`),
    }
  }

  /**
   * 
   * @returns {String} - the separator based on the language
   */
  getSeparator() {
    return this.lang == 'en-US' ? "." : ","
  }

  /**
   * Render the slider from the inline template, inkl. i18n and default settings
   * @param {*} beforeElement - the element before which the slider should be rendered
   */
  renderSlider(beforeElement) {
    const inputMinKgNum = Cnf.kg.min
    const inputMaxKgNum = Cnf.kg.max

    const sliderDiv = document.createElement("div");
    sliderDiv.classList.add("phoenix-dumbell-slider");

    const htmlTemplate = `
      <label for="${Cnf.sliderFromSliderId}">${i18n.weight_range}</label>
      <div class="range_container">
          <div class="sliders_control">
            <input id="${Cnf.sliderFromSliderId}" type="range" value="${inputMinKgNum}" min="${inputMinKgNum}" max="${inputMaxKgNum}" step="1"/>
            <input id="${Cnf.sliderToSliderId}" type="range" value="${inputMaxKgNum}" min="${inputMinKgNum}" max="${inputMaxKgNum}" step="1"/>
          </div>
          <div class="form_control">
            <div class="form_control_container">
                <div class="form_control_container__time">Min</div>
                <input class="form_control_container__time__input" type="number" id="${Cnf.sliderFromInputId}" value="${inputMinKgNum}" min="${inputMinKgNum}" max="${inputMaxKgNum}" step="1"/>
              </div>
              <div class="form_control_container">
                <div class="form_control_container__time">Max</div>
                <input class="form_control_container__time__input" type="number" id="${Cnf.sliderToInputId}" value="${inputMaxKgNum}" min="${inputMinKgNum}" max="${inputMaxKgNum}" step="1"/>
              </div>
          </div>
      </div>
    `
    sliderDiv.innerHTML = htmlTemplate
    beforeElement.parentNode.insertBefore(sliderDiv, beforeElement.nextSibling);
  }

  /**
   * Render the custom attributes from the inline template, inkl. i18n.
   * @param {*} afterElement - the element after which the custom attributes should be rendered
   */
  renderCustomAttributes(afterElement) {
    const customAttributesTemplate = `
    <div class="product__custom-attribute" id="${Cnf.customAttributes.totalPairsId}">
      <div class="product__custom-attribute__label">${i18n.weight_pairs}</div>
      <div class="product__custom-attribute__value">-</div>
    </div>
    <div class="product__custom-attribute" id="${Cnf.customAttributes.weightsListId}">
      <div class="product__custom-attribute__label">${i18n.weights_list}</div>
      <div class="product__custom-attribute__value">-</div>
    </div>
    <div class="product__custom-attribute" id="${Cnf.customAttributes.totalWeightId}">
      <div class="product__custom-attribute__label">${i18n.weight_total}</div>
      <div class="product__custom-attribute__value">-</div>
    </div>
  `

    const dumbellCounterDiv = document.createElement("div");
    dumbellCounterDiv.classList.add("phoenix-dumbell-counter");
    dumbellCounterDiv.innerHTML = customAttributesTemplate

    afterElement.nextSibling.insertAdjacentElement('afterend', dumbellCounterDiv);
  }

  /**
   * Update the slider fill based on the current values
   * @param {Object} from - the from object with value, min, max
   * @param {Object} to - the to object with value, min, max
   * @param {Object} controlSlider - the slider to control
   */
  updateSliderFill(from, to, controlSlider) {
    const rangeDistance = to.max - to.min;
    const fromPosition = from.value - to.min;
    const toPosition = to.value - to.min;
    controlSlider.style.background = `linear-gradient(
      to right,
      ${Cnf.sliderColor} 0%,
      ${Cnf.sliderColor} ${(fromPosition) / (rangeDistance) * 100}%,
      ${Cnf.sliderRangeColor} ${((fromPosition) / (rangeDistance)) * 100}%,
      ${Cnf.sliderRangeColor} ${(toPosition) / (rangeDistance) * 100}%, 
      ${Cnf.sliderColor} ${(toPosition) / (rangeDistance) * 100}%, 
      ${Cnf.sliderColor} 100%)`;
  }

  /**
   * Update the slider attributes (min, max) based on the weight unit
   * when the weight unit is changed (e.g. kg is 5-100 while lbs is 10-200),
   * Ensures that the values are resetted into those constraints.
   */
  updateSliderAttributes() {
    const { fromSlider, toSlider, fromInput, toInput } = this.getTemplateElements()

    const domElementsToUpdate = [fromSlider, toSlider, fromInput, toInput]

    if (Number(fromSlider.value) < Cnf[this.weightUnit].min) {
      fromSlider.value = Cnf[this.weightUnit].min
      fromInput.value = Cnf[this.weightUnit].min
    }
    if (Number(toSlider.value) > Cnf[this.weightUnit].max) {
      toSlider.value = Cnf[this.weightUnit].max
      toInput.value = Cnf[this.weightUnit].max
    }

    for (const domElement of domElementsToUpdate) {
      for (const attributeKey of Cnf.sliderAttributeKeys) {
        domElement.setAttribute(attributeKey, Cnf[this.weightUnit][attributeKey])
      }
    }

    this.updateSliderFill(fromSlider, toSlider, toSlider);
  }

  /**
   * TODO: Find out how this exactly works and when it needs to be called
   * Update the slider accessibility based on the current target
   * @param {*} currentTarget - the current target, slider or input
   */
  updateSliderAccessibility(currentTarget) {
    const toSlider = document.querySelector('#toSlider');
    if (Number(currentTarget.value) <= 0) {
      toSlider.style.zIndex = 2;
    } else {
      toSlider.style.zIndex = 0;
    }
  }

  /**
   * Update the checkboxes based on the current slider values
   * Iterates from min to max using the step value and updates the checkboxes
   * Updates the total pairs, total weight and weights list
   */
  updateCheckboxes() {

    // const baseSel = this.weightUnit === "kg" ? Cnf.kgWeightsSel : Cnf.lbsWeightsSel
    const baseSel = Cnf[this.weightUnit].weightsSelector
    for (const checkbox of [...document.querySelectorAll(`.${baseSel} input`)]) {
      checkbox.checked = false
    }

    const [from, to] = this.getParsed(fromSlider, toSlider);

    const stepValue = this.getStepValueSetting()

    // iterate from min to max using the step value
    let pairCounter = 0
    let totalWeight = 0
    let weightsList = []
    for (let i = from; i <= to; i += stepValue) {
      const checkbox = document.querySelector(`#${baseSel}_${String(i).replace(".", "_")}`)
      if (checkbox) {
        pairCounter += 1
        totalWeight += i
        weightsList.push(i)
        if (i + stepValue > to) {
          checkbox.click();
        } else {
          checkbox.checked = true
        }
      }
    }

    this.updateTotalPairs(pairCounter);
    this.updateTotalWeight(totalWeight);
    this.updateWeightsList(weightsList);
  }

  /**
   * Update the total weight based on the current total weight
   * @param {Number} totalWeight - the total weight
   */
  updateTotalWeight(totalWeight) {
    const parent = document.getElementById(Cnf.customAttributes.totalWeightId)
    const valueEl = parent.querySelector(`.${Cnf.customAttributes.valueClass}`)
    valueEl.innerText = (totalWeight * 2) + "" + this.weightUnit
  }

  /**
   * Update the total pairs based on the current pair count
   * @param {Number} pairCount - the pair count 
   */
  updateTotalPairs(pairCount) {
    const parent = document.getElementById(Cnf.customAttributes.totalPairsId)
    const valueEl = parent.querySelector(`.${Cnf.customAttributes.valueClass}`)
    valueEl.innerText = pairCount
  }

  /**
   * Update the weights list based on the current weights list, separated by the separator,
   * and formatted as em tags, e.g. <em>5.0kg</em>
   * @param {Array} weightsList - the weights list
   */
  updateWeightsList(weightsList = []) {
    // format the list
    const separator = this.getSeparator()
    weightsList = weightsList.map((weight) => {
      return `<em>${weight.toString().replace(".", separator)}${this.weightUnit}</em>`
    })
    // render the list
    const weightListNode = document.querySelector(Cnf.weightsListValueSel)
    weightListNode.innerHTML = weightsList.join(` `)
  }

  /** 
   * Register the slider change events
   * - from slider
   * - to slider
   * - from input
   * - to input
   */
  registerSliderChangeEvents() {
    const { fromSlider, toSlider, fromInput, toInput } = this.getTemplateElements()
    fromSlider.oninput = () => this.controlFromSlider(fromSlider, toSlider, fromInput, toInput);
    toSlider.oninput = () => this.controlToSlider(fromSlider, toSlider, toInput, fromInput);
    // fromInput.oninput = () => this.controlFromInput(fromSlider, fromInput, toInput, toSlider);
    // toInput.oninput = () => this.controlToInput(toSlider, fromInput, toInput, toSlider);
  }

  /**
   * Register the step change events
   * - on step change update the step value of the sliders / inputs under the constraints
   * - handle the special case with 2kg steps
   * - update the slider fill
   * - update the slider accessibility
   */
  registerStepChangeEvents() {
    const { fromSlider, toSlider, fromInput, toInput } = this.getTemplateElements()
    // kg+lbs - on step change update the step value of the sliders / inputs under the constraints
    for (const radioButton of [...document.querySelectorAll(Cnf.allStepRadioSelector)]) {
      radioButton.addEventListener('click', (e) => {

        const stepValueAsString = e.target.value

        let parsedStepValue = parseFloat(stepValueAsString.replace("_", "."))

        // special rule for 2
        let sliderStepValue = parsedStepValue
        if (parsedStepValue === 2.0) {
          sliderStepValue = 1.0
        }

        for (const controlElement of [fromSlider, toSlider, fromInput, toInput]) {
          controlElement.setAttribute('step', sliderStepValue)
        }

        const startValue = parseFloat(fromSlider.getAttribute('min'))

        // *** min contraints ***
        const minVal = Math.floor(fromSlider.value / sliderStepValue) * sliderStepValue + startValue % sliderStepValue
        fromSlider.value = minVal
        fromInput.value = minVal

        // *** max contraints ***
        let maxVal = Math.floor(toSlider.value / sliderStepValue) * sliderStepValue + startValue % sliderStepValue

        // special rule for 2
        if (parsedStepValue == 2.0) {
          if ((maxVal - minVal) % 2 != 0) {
            maxVal -= 1
          }
        }

        toSlider.value = maxVal
        toInput.value = maxVal

        this.updateSliderFill(minVal, maxVal, toSlider)
        this.updateSliderAccessibility(toSlider)
      })
    }
  }

  /**
   * Register the radio change events which update the checkboxes (debounced)
   */
  registerRadioChangeEvents() {
    document.querySelectorAll(Cnf.radiosThatTriggerUpdatesSelector).forEach(element => {
      element.addEventListener('click', () => {
        this.debouncedUpdateCheckboxes();
      });
    });
  }

  /**
   * Register the weight change events which update weight unit and slider attributes
   */
  registerWeightChangeEvents() {
    // on weight unit change
    document.querySelectorAll(Cnf.weightUnitRadioSelector).forEach(element => {
      element.addEventListener('click', () => {
        this.weightUnit = element.value
        this.updateSliderAttributes()
      });
    });
  }

  enhanceRadioButtonsWithCheckedClass() {
    // add -checked class on the label of a radio button
    document.querySelectorAll('.extra-options input[type="radio"]').forEach(element => {

      if (element.getAttribute("checked") === "checked") {
        element.parentElement.classList.add("-checked")
      }

      element.addEventListener('click', function (event) {

        const siblings = Array.from(event.target.parentElement.parentElement.children);
        siblings.forEach((element) => {
          element.classList.remove("-checked")
        });

        if (event.target.checked) {
          event.target.parentElement.classList.add("-checked")
        }
      });
    });
  }

  /**
   * When from (min) input is changed, update the slider
   * Note: Currently input fields are not used
   */
  controlFromInput(fromSlider, fromInput, toInput, controlSlider) {
    const [from, to] = this.getParsed(fromInput, toInput);
    this.updateSliderFill(fromInput, toInput, controlSlider);
    if (from > to) {
      fromSlider.value = to;
      fromInput.value = to;
    } else {
      fromSlider.value = from;
    }
  }

  /**
   * When to (max) input is changed, update the slider
   * Note: Currently input fields are not used
   */
  controlToInput(toSlider, fromInput, toInput, controlSlider) {
    const [from, to] = this.getParsed(fromInput, toInput);
    this.updateSliderFill(fromInput, toInput, controlSlider);
    this.updateSliderAccessibility(toInput);
    if (from <= to) {
      toSlider.value = to;
      toInput.value = to;
    } else {
      toInput.value = from;
    }
  }

  /**
   * When from (min) slider is changed, update the slider and input
   * Handle special case for 2kg steps
   * Trigger the update for checkboxes
   */
  controlFromSlider(fromSlider, toSlider, fromInput, toInput) {
    let [from, to] = this.getParsed(fromSlider, toSlider);

    // special logic for 2 steps
    const stepValue = this.getStepValueSetting();
    if (stepValue == 2) {
      if ((to - from) % 2 != 0) {
        to -= 1
      }
      toInput.value = to;
    }

    this.updateSliderFill(fromSlider, toSlider, toSlider);
    if (from > to) {
      fromSlider.value = to;
      fromInput.value = to;
    } else {
      fromInput.value = from;
    }
    this.debouncedUpdateCheckboxes()
  }

  /**
   * When to (max) slider is changed, update the slider and input
   * Handle special case for 2kg steps
   * Trigger the update for checkboxes
   */
  controlToSlider(fromSlider, toSlider, toInput, fromInput) {
    let [from, to] = this.getParsed(fromSlider, toSlider);

    // special logic for 2 steps
    const stepValue = this.getStepValueSetting();
    if (stepValue == 2) {
      if ((to - from) % 2 != 0) {
        from += 1
      }
      fromInput.value = from;
    }

    this.updateSliderFill(fromSlider, toSlider, toSlider);
    this.updateSliderAccessibility(toSlider);
    if (from <= to) {
      toSlider.value = to;
      toInput.value = to;
    } else {
      toInput.value = from;
      toSlider.value = from;
    }
    this.debouncedUpdateCheckboxes()
  }

}
